import { A } from '@adornis/base/env-info.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { BehaviorSubject, takeUntil } from 'rxjs';

export interface ContextMenuItem {
  icon?: string;
  label: string;
  background?: ColorType;
  action: () => Promise<void> | void;
  enabled?: boolean;
}

export const openContextMenu = new BehaviorSubject('');

/**
 * @element chancen-component-context-menu
 */
@customElement('chancen-component-context-menu')
export class ChancenComponentContextMenu extends ChemistryLitElement {
  @property({ attribute: false }) public items: ContextMenuItem[] = [];

  @property() public mode = 'icon';
  @property() public icon = 'menu_open';

  private readonly _refContextMenu: Ref<XDropdown> = createRef();

  private _firstVisited = false;

  @state() private _globalOpenContextMenu = new RXController(this, openContextMenu);

  // This id is used for deduplication between components. It is used in the global subject, so only one component is open at the same time.
  @property({ type: String, attribute: 'menu-id', reflect: true }) private readonly _menuID = A.getGloballyUniqueID();

  override styles() {
    return [...super.styles(), { ':host': { placeContent: 'center' } }];
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this._globalOpenContextMenu.observable.pipe(takeUntil(this.disconnected)).subscribe(openMenu => {
      if (openMenu !== this._menuID) this._refContextMenu.value?.close();
    });
  }

  override render() {
    if (!this.items.length) return nothing;
    return html`
      <x-dropdown ${ref(this._refContextMenu)} @close=${() => this.requestUpdate()}>
        <cr-flex ${css({ position: 'relative', width: '0', height: '0' })}>
          <cr-flex
            @mouseleave=${() => {
              // Pseudo element for hovering from the gap between the button to the context menu
              if (!this._refContextMenu.value?.isOpen()) return;

              setTimeout(() => {
                if (!this._firstVisited) {
                  void this._refContextMenu.value?.close();
                }
              }, 250);
            }}
            ${css({
              position: 'absolute',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              top: '-15px',
              left: '-22px',
            })}
          >
          </cr-flex>
        </cr-flex>

        <cr-button
          ${css({ color: this.mode === 'icon' ? this.colors.secondary : 'white' })}
          mode=${this.mode}
          icon=${this.icon}
          ?big=${this.mode === 'icon'}
          @click=${() => {
            void this._refContextMenu.value?.toggle();
          }}
          @mouseover=${() => {
            if (!this._refContextMenu.value?.isOpen()) {
              void this._refContextMenu.value?.open();
              openContextMenu.next(this._menuID);
            }
          }}
        >
          <slot></slot>
        </cr-button>

        <cr-flex
          ${css({
            backdropFilter: 'blur(4px)',
            background: this.colors.neutral.shade(50).opacity(0.5),
            boxShadow: '0px 4px 18px 0px rgba(0,0,0,0.75)',
            borderRadius: this.sizes.borderRadiusSecondary,
          })}
          slot="dropdown-content"
          @mouseover=${() => {
            if (!this._firstVisited) {
              this._firstVisited = true;
            }
          }}
          @mouseleave=${() => {
            void this._refContextMenu.value?.close();
            this._firstVisited = false;
          }}
        >
          ${repeat(
            this.items,
            i => i.label,
            (item, index) => {
              const enabled = item.enabled === undefined || item.enabled;
              return html`
                <chancen-component-context-menu-item
                  .item=${item}
                  .first=${index === 0}
                  .last=${index === this.items.length - 1}
                  ${css({ opacity: enabled ? 1 : 0.4, pointerEvents: enabled ? 'all' : 'none' })}
                  @clicked=${() => {
                    void this._refContextMenu.value?.close();
                  }}
                ></chancen-component-context-menu-item>
              `;
            },
          )}
        </cr-flex>
      </x-dropdown>
    `;
  }
}

/**
 * @element chancen-component-context-menu-item
 */
@customElement('chancen-component-context-menu-item')
export class ChancenComponentContextMenuItem extends ChemistryLitElement {
  @property({ attribute: false }) public item!: ContextMenuItem;
  @property({ attribute: false }) public last = false;
  @property({ attribute: false }) public first = false;

  override styles() {
    return [
      ...super.styles(),
      {
        '.item': {
          transition: 'background 0.125s',
          background: 'transparent',
          cursor: 'pointer',
          paddingInline: this.spacing.sm,
          paddingTop: this.first ? this.spacing.md : this.spacing.sm,
          paddingBottom: this.last ? this.spacing.md : this.spacing.sm,
          borderRadius:
            this.first && this.last
              ? `${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary}`
              : this.first
                ? `${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary} 0px 0px`
                : this.last
                  ? ` 0px 0px ${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary}`
                  : 'unset',
        },
        '.item:hover': { background: this.item.background ?? this.colors.primary.shade(400), color: 'white' },
      },
    ];
  }

  override render() {
    return html`
      <cr-flex
        space="sm"
        class="item"
        horizontal
        crossaxis-center
        @click=${async () => {
          await this.item.action();

          const event = new CustomEvent('clicked', {});
          this.dispatchEvent(event);
        }}
      >
        <cr-icon>${this.item.icon}</cr-icon>
        <cr-text>${this.item.label}</cr-text>
      </cr-flex>
    `;
  }
}
